import el from "@cypherpotato/el";
import page from "page";
import { api } from "../../api";
import { Button, ButtonRow, ToggleButton } from "../../components/button";
import { Card, CardNavigation } from "../../components/card";
import { ChatV2 } from "../../components/chat.v2";
import { CommandBar } from "../../components/command-bar";
import { ConversationInfoMessage, ConversationMessage, getConversationMessageWeight, getConversationUsageWeight } from "../../components/conversation";
import { Dashboard } from "../../components/dashboard";
import { DateTimeLabel } from "../../components/datetime-label";
import { Definition, DefinitionList } from "../../components/definition";
import { Dropdown, DropdownItem } from "../../components/dropdown";
import { Icon } from "../../components/icon";
import { Padding } from "../../components/layout";
import { Playground, PlaygroundAside, PlaygroundAsideContainer, PlaygroundContent } from "../../components/playground";
import { HorizontalSizableContainer, HorizontalSizableSection, SizableContainer, SizableSection } from "../../components/sizable-container";
import { TabControl } from "../../components/tabcontrol";
import { CopiableText, MutedText } from "../../components/text";
import { getSession } from "../../session";
import { formatNumber } from "../../ui/format";
import { runOperation } from "../../ui/operation";
import { JsonControl } from "../../components/json-control";
import { ResourceBadge } from "../../components/resource-badge";
import { copyToClipboard } from "../../ui/clipboard";
import { PerfWaterfall } from "../../components/perf-waterfall";
import { displayAlert } from "../../ui/alert";
import { ProviderBadge } from "../../components/model-icon";
import { LiveConversationStatusBadge, TagBadge, TagIconBadge } from "../../components/badge";
import { cond } from "../../util";

export default function (ctx) {

    const conversationId = ctx.params.id;
    const conversation = el.state.create(null);
    const heatMapEnabled = el.state.create(false);
    const chatModeEnabled = el.state.create(false);
    let heatMapToggle;
    let chatModeToggle;
    let eventSource;

    runOperation("Loading conversation...", async () => {
        const response = await api.getJson(`/api/v1/conversations/${conversationId}`);
        conversation.value = response.data;

        if (conversation.value.status && conversation.value.status != "completed") {
            eventSource = api.createEventSource(`/api/v1/conversations/${conversationId}/sse`);
            eventSource.onmessage = event => {
                conversation.value = JSON.parse(event.data).data;
                if (conversation.value.status == "completed") {
                    eventSource.close();
                    conversation.value.conversationId && page.redirect("/dashboard/conversations/" + conversation.value.conversationId);
                }
            };
        }
    })

    return Dashboard(
        {
            classList: ["full-size"],
            mount(element) {
                const observer = new MutationObserver(() => {
                    if (!element.isConnected) {
                        eventSource?.close();
                        observer.disconnect();
                    }
                });
                observer.observe(document.body, { childList: true, subtree: true });
            }
        },
        Card(
            CardNavigation(
                CommandBar(
                    ButtonRow(
                        Button(
                            Icon("arrow-left-line"),
                            "Back to conversations",
                            {
                                onClick() {
                                    history.go(-1);
                                }
                            }
                        )
                    ),
                    ButtonRow(
                        Button(
                            Icon("expand-diagonal-line"),
                            "Expand",
                            {
                                onClick() {
                                    document.querySelectorAll("details.conversation-message").forEach(el => el.open = true);
                                }
                            }
                        ),
                        Button(
                            Icon("collapse-diagonal-line"),
                            "Minimize",
                            {
                                onClick() {
                                    document.querySelectorAll("details.conversation-message").forEach(el => el.open = false);
                                }
                            }
                        ),
                        heatMapToggle = ToggleButton(
                            Icon("fire-line"),
                            "Heat map",
                            {
                                value: false,
                                onToggle(state) {
                                    if (state.value) {
                                        chatModeToggle.value = false;
                                    }
                                    heatMapEnabled.value = state.value;
                                    document.querySelectorAll("details.conversation-message").forEach(el => el.open = false);
                                }
                            }
                        ),
                        chatModeToggle = ToggleButton(
                            Icon("chat-1-line"),
                            "Chat mode",
                            {
                                value: false,
                                onToggle(state) {
                                    if (state.value) {
                                        heatMapToggle.value = false;
                                    }
                                    chatModeEnabled.value = state.value;
                                }
                            }
                        )
                    ),
                    ButtonRow(
                        Dropdown("Export",
                            DropdownItem("Export as JSON", Icon("braces-line"),
                                {
                                    onClick() {
                                        window.open(window.env.apiEndpoint + `/api/v1/conversations/${conversationId}/export.json?api-key=${getSession().sessionKey}`, "_blank");
                                    }
                                }
                            ),
                            DropdownItem("Copy to clipboard", Icon("file-copy-line"),
                                {
                                    onClick() {
                                        copyToClipboard(JSON.stringify(conversation.value, null, 4))
                                    }
                                }
                            )
                        )
                    )
                )
            ),
            Playground(
                HorizontalSizableContainer(
                    HorizontalSizableSection(
                        {
                            initialSize: "400px"
                        },
                        PlaygroundAside(
                            el.state.listening([conversation], () =>
                                TabControl([
                                    {
                                        title: "Conversation data",
                                        icon: Icon("list-unordered"),
                                        contents: PlaygroundAsideContainer(
                                            el.state.listening([conversation], () =>
                                                SizableContainer(
                                                    SizableSection("Conversation data",
                                                        conversation.value && Padding(0.5,
                                                            DefinitionList(
                                                                Definition("ID", CopiableText('#' + conversation.value.id.substr(-4), conversation.value.id)),
                                                                conversation.value.status && Definition("Status", LiveConversationStatusBadge(conversation.value)),
                                                                conversation.value.requestId && Definition("Request ID", CopiableText('#' + conversation.value.requestId.substr(-4), conversation.value.requestId)),
                                                                Definition("Model name", CopiableText(conversation.value.modelName)),
                                                                Definition("Provider", conversation.value.providerName && ProviderBadge(conversation.value.providerName) || el.fragment(Icon("key-2-line"), " BYOK")),
                                                                conversation.value.providerDataCollection && Definition("Data collection", cond(
                                                                    [conversation.value.providerDataCollection[0] == "Zero", TagIconBadge(Icon("shield-line"), " Zero data retention", "var(--success)")],
                                                                    [conversation.value.providerDataCollection[0] == "PromptNoTraining", TagIconBadge(Icon("alert-line"), " Prompts, no training", "var(--warn)")],
                                                                    [conversation.value.providerDataCollection[0] == "PromptWithTraining", TagIconBadge(Icon("alert-line"), " Prompts with training", "var(--warn)")],
                                                                    [conversation.value.providerDataCollection[0] == "PromptWithPublishing", TagIconBadge(Icon("alert-line"), " Prompts with training, may publish prompts", "var(--warn)")],
                                                                    [true, TagIconBadge(Icon("question-line"), " Unknown", "var(--fr-300)")]
                                                                )),
                                                                Definition("Routing", {
                                                                    Balanced: "Balanced",
                                                                    Cheapest: "Lowest cost",
                                                                    Fastest: "Fastest",
                                                                    Quality: "Highest quality",
                                                                    Special: "Special"
                                                                }[conversation.value.routingOption?.[0]] || MutedText("None")),
                                                                Definition("Variant", conversation.value.modelVariant && TagBadge(conversation.value.modelVariant) || MutedText("None")),
                                                                Definition("Tag/Ext. User Id", CopiableText(conversation.value.externalUserId) || MutedText("Empty")),
                                                                Definition("Origin", CopiableText(conversation.value.origin[0])),
                                                                Definition("Created", DateTimeLabel(conversation.value.timestamps.createdAt)),
                                                                Definition("Updated", DateTimeLabel(conversation.value.timestamps.updatedAt)),
                                                                Definition("Token count", formatNumber(conversation.value.tokenCount), " total tokens", {
                                                                    title: "Total input and output tokens for this conversation, considering server-side tool re-calls."
                                                                }),
                                                            )
                                                        )
                                                    ),
                                                    SizableSection("Metadata",
                                                        conversation.value && Padding(0.5,
                                                            DefinitionList(
                                                                Object.entries(conversation.value.metadata).map(([key, value]) =>
                                                                    Definition(key, value, { style: { fontFamily: "monospace" } })
                                                                )?.nullWhenEmpty() || MutedText("No metadata")
                                                            )
                                                        )
                                                    )
                                                )
                                            )
                                        )
                                    },
                                    conversation.value?.usageObject?.prompt_tokens && {
                                        title: "Usage",
                                        icon: Icon("pie-chart-line"),
                                        contents: PlaygroundAsideContainer(
                                            el.state.listening([conversation], () =>
                                                SizableContainer(
                                                    SizableSection("Conversation usage",
                                                        Padding(0.5,
                                                            conversation.value?.usageObject && JsonControl({
                                                                value: conversation.value.usageObject,
                                                                readonly: true
                                                            }) || MutedText("No usage data")
                                                        )
                                                    ),
                                                    SizableSection("Resources",
                                                        Padding(0.5,
                                                            conversation.value?.resources?.length && conversation.value.resources.map(resource =>
                                                                Padding(0.25,
                                                                    ResourceBadge(resource.resourceId, resource.resourceType[0])
                                                                )
                                                            ) || MutedText("No resources")
                                                        )
                                                    )
                                                )
                                            )
                                        )
                                    },
                                    {
                                        title: "Response schema",
                                        icon: Icon("file-code-line"),
                                        contents: PlaygroundAsideContainer(
                                            el.state.listening([conversation], () =>
                                                SizableContainer(
                                                    SizableSection("Response schema",
                                                        Padding(0.5,
                                                            conversation.value?.responseSchema && JsonControl({
                                                                value: conversation.value.responseSchema,
                                                                readonly: true
                                                            }) || MutedText("No schema data")
                                                        )
                                                    )
                                                )
                                            )
                                        )
                                    },
                                    {
                                        title: "Tools",
                                        icon: Icon("wrench-line"),
                                        contents: PlaygroundAsideContainer(
                                            el.state.listening([conversation], () =>
                                                SizableContainer(
                                                    SizableSection("Tool list",
                                                        Padding(0.25,
                                                            conversation.value?.tools?.length && conversation.value.tools.map(tool =>
                                                                Padding(0.25,
                                                                    Button(Icon("braces-line"), tool, {
                                                                        onClick() {
                                                                            const inputSchema = conversation.value.toolInputSchemas?.[tool];
                                                                            displayAlert({
                                                                                title: `${tool} input schema`,
                                                                                dialogSize: "large",
                                                                                message: inputSchema && JsonControl({
                                                                                    value: inputSchema,
                                                                                    readonly: true,
                                                                                    ariaLabel: `${tool} input schema`
                                                                                }) || MutedText("No input schema was stored for this tool.")
                                                                            });
                                                                        }
                                                                    })
                                                                )
                                                            )?.nullWhenEmpty() || MutedText("No tools data")
                                                        )
                                                    )
                                                )
                                            )
                                        )
                                    },
                                    !!conversation.value?.usageObject?.performance && {
                                        title: "Performance",
                                        icon: Icon("dashboard-2-line"),
                                        contents: PlaygroundAsideContainer(
                                            el.state.listening([conversation], () =>
                                                SizableContainer(
                                                    SizableSection("Request performance",
                                                        conversation.value?.usageObject?.performance && Padding(0.5,
                                                            PerfWaterfall({ performance: conversation.value.usageObject.performance })
                                                        ) || MutedText("No performance data")
                                                    )
                                                )
                                            )
                                        )
                                    }
                                ].filter(Boolean), {
                                    type: "vertical"
                                })
                            )
                        )
                    ),
                    HorizontalSizableSection(
                        {
                            initialSize: "calc(100% - 400px)"
                        },
                        el.state.listening([conversation, heatMapEnabled, chatModeEnabled], () =>
                            chatModeEnabled.value
                                ? PlaygroundContent(
                                    {
                                        classList: ["conversation-chat-mode"]
                                    },
                                    conversation.value && ChatV2({
                                        messageList: conversation.value.messages,
                                        readOnly: true
                                    })
                                )
                                : PlaygroundContent(
                                    conversation.value && [
                                    ...(() => {
                                        const messages = conversation.value.messages
                                            .filter(m => m.content || m.tool_calls?.length || m.skipped);
                                        const messageWeights = new Map();
                                        let pendingMessageWeight = 0;
                                        for (const m of messages) {
                                            if (m.skipped)
                                                continue;

                                            if (m.role == "assistant" && m.usage) {
                                                const usageWeight = getConversationUsageWeight(m.usage);
                                                if (usageWeight > 0) {
                                                    messageWeights.set(m, pendingMessageWeight);
                                                    pendingMessageWeight = 0;
                                                }
                                            }

                                            pendingMessageWeight += getConversationMessageWeight(m);
                                        }

                                        const maxMessageWeight = Math.max(0, ...messageWeights.values());
                                        let previousUsage = null;
                                        let previousGeneratedAt = null;

                                        return messages
                                            .map(m => {
                                                if (m.skipped)
                                                    return ConversationInfoMessage({ variant: 'info', icon: 'scissors-cut-line', label: `${m.skipped} messages omitted` });

                                                const trackMessageMetrics = m.role == "user" || m.role == "assistant";
                                                const messageWeight = messageWeights.get(m) ?? 0;
                                                const item = ConversationMessage({
                                                    ...m,
                                                    previousUsage: trackMessageMetrics ? previousUsage : null,
                                                    usageHeat: heatMapEnabled.value && messageWeight > 0 && maxMessageWeight > 0
                                                        ? messageWeight / maxMessageWeight
                                                        : null,
                                                    heatMapEnabled: heatMapEnabled.value,
                                                    previousGeneratedAt: trackMessageMetrics ? previousGeneratedAt : null
                                                });
                                                if (m.role == "user") {
                                                    previousUsage = null;
                                                } else if (m.role == "assistant" && m.usage) {
                                                    previousUsage = m.usage;
                                                }
                                                if (trackMessageMetrics && m.generated_at)
                                                    previousGeneratedAt = m.generated_at;

                                                return item;
                                            });
                                    })(),
                                    conversation.value.errorMessage && ConversationInfoMessage({
                                        variant: 'error',
                                        icon: 'error-warning-line',
                                        label: 'Request error',
                                        content: conversation.value.errorMessage
                                    })
                                    ].filter(Boolean)
                                )
                        )
                    )
                )
            )
        )
    )
}
